import { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';

const STATUS_PILL = {
  pending:   { cls: 's-bk', label: 'Pending',   color: 'var(--am)' },
  processed: { cls: 's-ac', label: 'Processed', color: 'var(--gr)' },
  declined:  { cls: 's-ov', label: 'Declined',  color: 'var(--ac)' },
};

const SCENARIO_LABEL = {
  full:    '🚫 Full cancellation',
  partial: '✂️ Partial — date change shortening',
  late:    '⏰ Late cancellation',
  no_show: '👻 No-show',
};

const PAY_METHOD_LABEL = {
  stripe:      '💳 Stripe',
  viva:        '🔵 Viva',
  paypal:      '🅿️ PayPal',
  online:      '🌐 Online',
  bank:        '🏦 Bank Transfer',
  cash:        '💵 Cash',
  paymentlink: '🔗 Payment Link',
};

export default function RefundsPage() {
  const { refunds = [], setRefunds, currentUserId, getUserName, showToast, fetchAllData } = useApp();

  const [search, setSearch]         = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [editRefund, setEditRefund] = useState(null);   // { id, amount } when adjusting
  const [declineFor, setDeclineFor] = useState(null);   // refund id to decline
  const [declineReason, setDeclineReason] = useState('');

  // ── Stats ─────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const pending   = refunds.filter(r => r.status === 'pending');
    const processed = refunds.filter(r => r.status === 'processed');
    const declined  = refunds.filter(r => r.status === 'declined');
    const pendingTotal   = pending.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    const processedTotal = processed.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    return {
      total:      refunds.length,
      pending:    pending.length,
      processed:  processed.length,
      declined:   declined.length,
      pendingTotal,
      processedTotal,
    };
  }, [refunds]);

  // ── Filtering ─────────────────────────────────────────────────────
  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => refunds.filter(r => {
    if (filterStatus && r.status !== filterStatus) return false;
    if (q) {
      const hay = [r.customer, r.email, r.rentalId, r.bookingRef, r.vehicleName, r.id].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [refunds, filterStatus, q]);

  // Sort: pending first (oldest at top), then processed, then declined
  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const statusOrder = { pending: 0, processed: 1, declined: 2 };
    const so = (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
    if (so !== 0) return so;
    return new Date(a.createdAt) - new Date(b.createdAt);
  }), [filtered]);

  // ── Actions ───────────────────────────────────────────────────────
  function markProcessed(id, overrideAmount = null) {
    // Optimistic update
    setRefunds(prev => prev.map(r => r.id === id ? {
      ...r, status: 'processed',
      amount: overrideAmount != null ? overrideAmount : r.amount,
      processedAt: new Date().toISOString(),
      processedBy: currentUserId,
    } : r));
    setEditRefund(null);
    // Persist to API
    const refund = refunds.find(r => r.id === id);
    const backendId = refund?._backendId || id;
    import('../api/services').then(({ refundService }) => {
      refundService.process(backendId, { amount: overrideAmount, processed_by_user_id: currentUserId }).catch(() => {});
    });
    showToast('✓ Refund marked as processed');
  }

  function decline(id, reason) {
    // Optimistic update
    setRefunds(prev => prev.map(r => r.id === id ? {
      ...r, status: 'declined',
      processedAt: new Date().toISOString(),
      processedBy: currentUserId,
      declineReason: reason || '(no reason given)',
    } : r));
    setDeclineFor(null);
    setDeclineReason('');
    // Persist to API
    const refund = refunds.find(r => r.id === id);
    const backendId = refund?._backendId || id;
    import('../api/services').then(({ refundService }) => {
      refundService.decline(backendId, { reason }).catch(() => {});
    });
    showToast('✗ Refund declined');
  }

  return (
    <div>
      <PageHeader
        title="Refunds"
        icon="💸"
        subtitle="Customer cancellations and partial refunds awaiting processing. Process each one in your payment provider (Stripe / Viva / bank) and mark it done here so the customer record stays accurate."
      />

      {/* Stats — clickable filters */}
      <div className="sg sg-4" style={{ marginBottom: 14 }}>
        <StatCard
          label="Total refunds" value={stats.total}
          desc={(filterStatus || search) ? 'Click to clear filters' : 'All refund requests'}
          active={!filterStatus && !search}
          onClick={() => { setFilterStatus(''); setSearch(''); }}
        />
        <StatCard
          label="Pending" value={stats.pending} variant="am"
          desc={filterStatus === 'pending' ? 'Filtering — click to clear' : (stats.pendingTotal ? `€${stats.pendingTotal.toFixed(2)} to process` : 'No action needed')}
          active={filterStatus === 'pending'}
          onClick={() => setFilterStatus(filterStatus === 'pending' ? '' : 'pending')}
        />
        <StatCard
          label="Processed" value={stats.processed} variant="gr"
          desc={filterStatus === 'processed' ? 'Filtering — click to clear' : (stats.processedTotal ? `€${stats.processedTotal.toFixed(0)} refunded total` : 'Completed refunds')}
          active={filterStatus === 'processed'}
          onClick={() => setFilterStatus(filterStatus === 'processed' ? '' : 'processed')}
        />
        <StatCard
          label="Declined" value={stats.declined} variant="ac"
          desc={filterStatus === 'declined' ? 'Filtering — click to clear' : 'Operator rejected'}
          active={filterStatus === 'declined'}
          onClick={() => setFilterStatus(filterStatus === 'declined' ? '' : 'declined')}
        />
      </div>

      {/* Toolbar */}
      <div className="tb2">
        <div className="srch">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input type="text" placeholder="Search customer, booking ref, vehicle..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="fsel" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="pending">⏳ Pending</option>
          <option value="processed">✅ Processed</option>
          <option value="declined">❌ Declined</option>
        </select>
      </div>

      {/* List */}
      {sorted.length === 0 ? (
        <div className="empty">
          <div className="empty-ico">💸</div>
          <div className="empty-title">{refunds.length === 0 ? 'No refunds yet' : 'No refunds match your filters'}</div>
          <div className="empty-sub">
            {refunds.length === 0
              ? 'When a customer cancels online or you process a partial refund, it will land here for your action.'
              : 'Try clearing some filters above.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sorted.map(r => {
            const st = STATUS_PILL[r.status] || STATUS_PILL.pending;
            const isPending = r.status === 'pending';
            const isEditing = editRefund?.id === r.id;
            const isDeclining = declineFor === r.id;
            return (
              <div key={r.id} style={{
                background: 'var(--sur)', border: `1.5px solid ${isPending ? 'var(--am-b)' : 'var(--bd)'}`,
                borderRadius: 10, padding: '12px 14px',
                boxShadow: isPending ? '0 2px 6px rgba(245, 158, 11, 0.08)' : 'none',
              }}>
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{r.customer || '(unknown customer)'}</span>
                      <span className={`sp ${st.cls}`}>{st.label}</span>
                      {r.bookingRef && <span style={{ fontSize: 10, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace" }}>Booking {r.bookingRef}</span>}
                      {r.rentalId && <span style={{ fontSize: 10, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace" }}>· {r.rentalId}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--tx3)' }}>
                      {SCENARIO_LABEL[r.scenario] || r.scenario}
                      {r.vehicleName && ` · ${r.vehicleName}`}
                      {r.email && ` · ${r.email}`}
                    </div>
                    {r.reason && <div style={{ fontSize: 11, color: 'var(--tx2)', marginTop: 4, fontStyle: 'italic' }}>"{r.reason}"</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: st.color, fontFamily: "'DM Mono',monospace", lineHeight: 1 }}>
                      €{(parseFloat(r.amount) || 0).toFixed(2)}
                    </div>
                    {r.originalAmount && r.originalAmount !== r.amount && (
                      <div style={{ fontSize: 9, color: 'var(--tx3)', textDecoration: 'line-through' }}>
                        of €{r.originalAmount.toFixed(2)} paid
                      </div>
                    )}
                  </div>
                </div>

                {/* Meta */}
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', fontSize: 10, color: 'var(--tx3)', paddingTop: 8, borderTop: '1px solid var(--bd)' }}>
                  <span><strong>Method:</strong> {PAY_METHOD_LABEL[r.payMethod] || r.payMethod || 'unknown'}</span>
                  {r.originalPaymentRef && <span><strong>Ref:</strong> <span style={{ fontFamily: "'DM Mono',monospace" }}>{r.originalPaymentRef}</span></span>}
                  <span><strong>Requested:</strong> {new Date(r.createdAt).toLocaleString('en-GB')}</span>
                  {r.processedAt && (
                    <span style={{ color: 'var(--gr)' }}>
                      <strong>{r.status === 'declined' ? 'Declined' : 'Processed'}:</strong> {new Date(r.processedAt).toLocaleString('en-GB')}
                      {r.processedBy && ` by ${getUserName?.(r.processedBy) || r.processedBy}`}
                    </span>
                  )}
                  {r.declineReason && (
                    <span style={{ color: 'var(--ac)' }}>
                      <strong>Reason:</strong> {r.declineReason}
                    </span>
                  )}
                </div>

                {/* Actions — only for pending */}
                {isPending && !isEditing && !isDeclining && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, justifyContent: 'flex-end' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditRefund({ id: r.id, amount: r.amount })}>
                      ✏️ Edit amount
                    </button>
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--ac)' }} onClick={() => setDeclineFor(r.id)}>
                      ❌ Decline
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={() => markProcessed(r.id)}>
                      ✅ Mark Processed
                    </button>
                  </div>
                )}

                {/* Edit amount inline */}
                {isEditing && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center', justifyContent: 'flex-end' }}>
                    <span style={{ fontSize: 11, color: 'var(--tx3)' }}>Final refund amount:</span>
                    <input type="number" className="fi" style={{ width: 100 }} min="0" step="0.01"
                      value={editRefund.amount}
                      onChange={e => setEditRefund(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))} />
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditRefund(null)}>Cancel</button>
                    <button className="btn btn-primary btn-sm" onClick={() => markProcessed(r.id, editRefund.amount)}>
                      ✅ Process €{editRefund.amount.toFixed(2)}
                    </button>
                  </div>
                )}

                {/* Decline inline */}
                {isDeclining && (
                  <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--ac-l)', border: '1px solid var(--ac-b)', borderRadius: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ac)', marginBottom: 6 }}>
                      Decline this refund? Customer will be emailed.
                    </div>
                    <input type="text" className="fi" placeholder="Reason (e.g. outside cancellation policy, dispute pending)"
                      value={declineReason} onChange={e => setDeclineReason(e.target.value)}
                      style={{ marginBottom: 8 }} />
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setDeclineFor(null); setDeclineReason(''); }}>Cancel</button>
                      <button className="btn btn-primary btn-sm" style={{ background: 'var(--ac)' }} onClick={() => decline(r.id, declineReason)}>
                        Confirm Decline
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
